import { useQuery } from "@tanstack/react-query";
import type { TableItem } from "@/types/domain";
import { fetchTable } from "../services/baseApi";
import { queryKeys } from "../lib/queryKeys";
import type { Database } from "../types/supabase";

type TableName = keyof Database['public']['Tables'];

/**
 * Propiedades para el hook useTableData
 */
interface UseTableDataProps<T = unknown> {
  table: TableName;
  queryParams?: (string | number | Record<string, unknown>)[];
  orderBy?: string | { column: string; ascending?: boolean } | Array<{ column: string; ascending?: boolean }>;
  select?: string;
  staleTime?: number;
  refetchInterval?: number | false;
  customQueryFn?: () => Promise<T[]>;
  enabled?: boolean;
}

/**
 * Hook para obtener datos de una tabla de Supabase
 * Solo se encarga de la obtención de datos, no de las mutaciones
 *
 * NOTA: `data` siempre es un array (vacío por defecto), por lo que un array vacío
 * NO distingue entre "0 registros" y "fallo en el fetch". El caller DEBE chequear
 * `error` (y/o `isLoading`) para diferenciar ambos casos y notificar al usuario.
 */
export function useTableData<T extends TableItem>({
  table,
  queryParams = [],
  orderBy = "name",
  select = "*",
  staleTime,
  refetchInterval,
  customQueryFn,
  enabled = true,
}: UseTableDataProps<T>) {
  // Con un `select` distinto de "*" la clave lo incluye: dos lectores de la
  // misma tabla con columnas distintas compartían entrada y uno recibía las
  // columnas del otro. Con "*" se conserva la clave histórica; la invalidación
  // por prefijo ['tableData', tabla] sigue alcanzando ambas variantes.
  const baseKey = queryKeys.tableData(table, queryParams);
  const queryKey = select === "*" ? baseKey : [...baseKey, { select }];
  const { data, isLoading, error, isFetching, refetch } = useQuery({
    queryKey,
    queryFn: customQueryFn || (async () => {
      const response = await fetchTable<T>(table, {
        select,
        orderBy: Array.isArray(orderBy) 
          ? orderBy 
          : typeof orderBy === 'string' 
            ? orderBy 
            : orderBy 
              ? [orderBy] 
              : undefined
      });
      
      if (response.status === 'error') {
        throw new Error(response.error?.message || 'No se pudieron cargar los datos.');
      }
      
      return response.data || [];
    }),
    // Solo se pasan si vienen definidas: React Query mezcla las opciones con
    // spread y un `staleTime: undefined` pisaba el default global (5 min), así
    // que cada montaje volvía a pedir la tabla aunque los datos fueran recientes.
    ...(staleTime !== undefined && { staleTime }),
    ...(refetchInterval !== undefined && { refetchInterval }),
    enabled,
  });

  return {
    data: data ?? [],
    isLoading,
    error,
    isFetching,
    refetch,
  };
}
